import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import { type SkillSummary } from "@/shared/domain/skills";
import { Combobox, Field, FormSection } from "@/shared/foundation/primitives";
import textStyles from "@/shared/foundation/styles/textStyles.module.css";
import { agentFormSectionId } from "@/shared/navigation";
import { pluralize } from "@/shared/text";

import { type AgentFormValues } from "../agentFormSchema";
import { attachableSkills } from "../skills/agentSkills";
import { SkillRow } from "../skills/SkillRow";
import styles from "../skills/skills.module.css";
import { useAgentEligibleSkills } from "../skills/useAgentEligibleSkills";

import sections from "./sections.module.css";

/**
 * The org-shared skills the agent's AI may invoke. A private skill is not on
 * offer: it would not resolve for anyone else who launched the agent.
 *
 * A new row tracks the latest version; its picker pins an exact one after.
 */
export function SkillsSection() {
  const { control, getFieldState, formState } =
    useFormContext<AgentFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "skills" });
  const { query, skills: eligible } = useAgentEligibleSkills();
  const attached = useWatch<AgentFormValues, "skills">({
    control,
    name: "skills",
  });
  const byId = new Map(eligible.map((entry) => [entry.skill.skill_id, entry]));
  const offered = attachableSkills(eligible, attached);
  const listError = getFieldState("skills", formState).error;

  return (
    <FormSection
      title="What skills it can use"
      titleId={agentFormSectionId("skills")}
      hint="Org-shared procedures the agent's AI may invoke. Pin a version, or leave it on Latest to track the newest."
      actions={
        <span className={sections.count} data-testid="agent-skills-count">
          {fields.length} {pluralize("skill", fields.length)}
        </span>
      }
    >
      {fields.length > 0 && (
        <div
          className={styles.list}
          data-testid="agent-skills"
          data-count={fields.length}
        >
          {fields.map((field, index) => (
            <SkillRow
              key={field.id}
              index={index}
              skillId={attached[index]?.skill_id ?? ""}
              summary={byId.get(attached[index]?.skill_id ?? "")}
              onRemove={() => {
                remove(index);
              }}
            />
          ))}
        </div>
      )}

      <div data-testid="agent-add-skill">
        <Combobox<SkillSummary>
          items={offered}
          value={null}
          onValueChange={(chosen) => {
            if (chosen !== null) {
              append({ skill_id: chosen.skill.skill_id, version: null });
            }
          }}
          itemToStringLabel={(entry) => entry.skill.name}
          isItemEqualToValue={(item, value) =>
            item.skill.skill_id === value.skill.skill_id
          }
          disabled={!query.isSuccess || offered.length === 0}
          label={<span className={textStyles.overline}>Add a skill</span>}
          autoHighlight
          size="lg"
        >
          <Combobox.TriggerInput placeholder={addPlaceholder(query, offered)} />
          <Combobox.Content>
            <Combobox.Empty />
            <Combobox.List>
              {(entry: SkillSummary) => (
                <Combobox.Item key={entry.skill.skill_id} value={entry} />
              )}
            </Combobox.List>
          </Combobox.Content>
        </Combobox>
      </div>

      {query.isError && (
        <p className={sections.muted} data-testid="agent-skills-error">
          Skills could not be loaded. {query.error.message}
        </p>
      )}

      {listError?.message !== undefined && (
        <Field.Root invalid>
          <Field.Error match>{listError.message}</Field.Error>
        </Field.Root>
      )}
    </FormSection>
  );
}

/** What the picker says, for the state of the list behind it. */
function addPlaceholder(
  query: ReturnType<typeof useAgentEligibleSkills>["query"],
  offered: readonly SkillSummary[],
): string {
  if (query.isPending) {
    return "Loading skills…";
  }
  if (query.isError) {
    return "Couldn’t load skills";
  }
  return offered.length === 0
    ? "No more skills to add"
    : "Search org-shared skills";
}
